import { useState, type ReactNode } from "react";
import { cx } from "./basics";

export interface CaseStep {
  id: string;
  text: string;
  expected_result?: string | null;
}

/**
 * A test case's steps as a numbered checklist. A step with an expected result shows it in a tinted
 * box right under the step, with its own tick. The ticks are a visual aid for the tester only:
 * they live in this component's state, are never sent anywhere and reset when the parent remounts
 * it (key it by the case id). `checkable` false is the read-only display (no checkboxes).
 * (docs/test-suite.md sections 7 and 8)
 */
export function CaseSteps({
  steps,
  checkable,
  label,
  expectedLabel = "Expected",
  note = true,
  namePrefix = "Step",
}: {
  steps: CaseStep[];
  checkable: boolean;
  /** The list's accessible name. */
  label: string;
  expectedLabel?: string;
  /** Show the "Ticks are just for you" line when checkable; off for a second list on one screen. */
  note?: boolean;
  /** The start of each checkbox name: "Step" gives "Step 1: ..." and "Step 1 expected: ...". */
  namePrefix?: string;
}) {
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <div className={cx("csteps", !checkable && "csteps-ro")}>
      <ol className="csteps-list" aria-label={label}>
        {steps.map((s, i) => {
          const n = i + 1;
          const exp = s.expected_result?.trim() ? s.expected_result : null;
          const expKey = `${s.id}:exp`;
          return (
            <li key={s.id} className="csteps-item">
              <StepRow
                checkable={checkable}
                checked={ticked.has(s.id)}
                onToggle={() => toggle(s.id)}
                aria={`${namePrefix} ${n}: ${s.text}`}
                lead={`${n}.`}
              >
                <span className={cx("csteps-text", ticked.has(s.id) && "csteps-done")}>{s.text}</span>
              </StepRow>
              {exp && (
                <div className="csteps-exp">
                  <StepRow
                    checkable={checkable}
                    checked={ticked.has(expKey)}
                    onToggle={() => toggle(expKey)}
                    aria={`${namePrefix} ${n} expected: ${exp}`}
                  >
                    <span className={cx("csteps-text", ticked.has(expKey) && "csteps-done")}>
                      <strong>{expectedLabel}:</strong> {exp}
                    </span>
                  </StepRow>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {checkable && note && steps.length > 0 && (
        <p className="muted csteps-note">Ticks are just for you; they aren't saved.</p>
      )}
    </div>
  );
}

function StepRow({
  checkable,
  checked,
  onToggle,
  aria,
  lead,
  children,
}: {
  checkable: boolean;
  checked: boolean;
  onToggle: () => void;
  aria: string;
  lead?: string;
  children: ReactNode;
}) {
  if (!checkable)
    return (
      <div className="csteps-row">
        {lead && <span className="csteps-num">{lead}</span>}
        {children}
      </div>
    );
  return (
    <label className="csteps-row csteps-check">
      <input type="checkbox" checked={checked} onChange={onToggle} aria-label={aria} />
      {lead && <span className="csteps-num">{lead}</span>}
      {children}
    </label>
  );
}
